import React, { useCallback, useEffect, useState } from 'react';
import './Styles.css';
import { IconButton, IconButtonSize, IconButtonType, IconButtonVariant } from '@ids/react-icon-button';
import { Ui16Cross } from '@ids/react-icons';
import SecondaryButton from '../../Elements/ids/Buttons/SecondaryButton';
import PrimaryButton from '../../Elements/ids/Buttons/PrimaryButton';
import { useParams } from 'react-router-dom';
import { formatMessage } from '../../Localization/formatMessage';
import Messages from '../../Localization/Messages';
import { PresentationMode } from '../../Enums/PresentationMode';
import { useGlobalContext } from '../../context/GlobalContext';
import BreadcrumbRow from '../../Elements/Breadcrumbs';
import { getLabelForSplitShare } from '../../Enums/SplitSalesShare';
import SplitSalesSearch from './SplitSalesSearch';
import Form from '../../Elements/Form';
import { areFieldsValid, getChangedFieldsForSplitData } from './Functions';
import { useDebouncedEffect } from '../../Helpers/CustomHooks';
import { ServiceClient } from '../../Services/ServiceClient';
import ErrorMessage from '../../Elements/ErrorMessage';
import { createServerErrorMessage } from '../../Helpers/FormInputHelper';
import LoaderComponent from '../../Elements/ids/Loader';
import SplitSalesShareSelector from './SplitSalesShareSelector/SplitSalesShareSelector';
import { SplitSalesPolicyState } from '../../Enums/SplitSalesPolicyState';

const SplitSalesPage = () => {
  const { policyVersion, policyState, agentCode } = useParams();

  const [pageMode, setPageMode] = useState(PresentationMode.Loading);
  const [splitData, setSplitData] = useState({ policyVersion: policyVersion, agentCode: agentCode });
  const [editedSplitData, setEditedSplitData] = useState(splitData);
  const [canSubmit, setCanSubmit] = useState(false);
  const [error, setError] = useState(null);
  const [searchKey, setSearchKey] = useState(0);

  const { addBreadcrumbs } = useGlobalContext();

  useEffect(() => {
    addBreadcrumbs([
      {
        trackingName: 'splitSales',
        link: '',
        title: formatMessage(Messages.breadcrumb_splitSalesPage),
      },
    ]);

    ServiceClient.getSplitSales(
      agentCode,
      policyVersion,
      result => {
        if (result.length === 0) {
          setPageMode(PresentationMode.Add);
          return;
        }
        setSplitData(result[0]);
        setEditedSplitData(result[0]);
        setPageMode(PresentationMode.View);
      },
      error => {
        setPageMode(PresentationMode.Error);
        setError(createServerErrorMessage(error).message);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateSplitData = useCallback(() => {
    const validationResult = areFieldsValid(editedSplitData);
    setCanSubmit(
      validationResult
        ? pageMode !== PresentationMode.Add
          ? getChangedFieldsForSplitData(editedSplitData, splitData) > 0
          : true
        : false
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedSplitData]);

  useDebouncedEffect(validateSplitData, 500);

  const onSave = () => {
    if (pageMode === PresentationMode.Add)
      ServiceClient.createSplitSale(
        editedSplitData,
        result => {
          setPageMode(PresentationMode.View);
          setSplitData(result);
          setEditedSplitData(result);
        },
        error => {
          setPageMode(PresentationMode.Error);
          setError(createServerErrorMessage(error).message);
        }
      );

    if (pageMode === PresentationMode.Edit)
      ServiceClient.editSplitSales(
        editedSplitData,
        result => {
          setPageMode(PresentationMode.View);
          setSplitData(result);
          setEditedSplitData(result);
        },
        error => {
          setPageMode(PresentationMode.Error);
          setError(createServerErrorMessage(error).message);
        }
      );
  };

  return (
    <>
      <BreadcrumbRow />

      <Form>
        <div className="splitSales-container">
          <div className="splitSales-firstItem-container">
            <div>
              <div className="splitSales-primaryInfo-title">
                {formatMessage(Messages.splitSales_policyVersionLabel)}
              </div>
              <div>{splitData.policyVersion}</div>
            </div>
            {pageMode === PresentationMode.Edit && (
              <IconButton
                type={IconButtonType.PLAIN}
                variant={IconButtonVariant.QUIET}
                size={IconButtonSize.SMALL}
                onClick={() => {
                  setPageMode(PresentationMode.View);
                  setEditedSplitData(splitData);
                }}>
                <Ui16Cross />
              </IconButton>
            )}
          </div>

          <div>
            <div className="splitSales-primaryInfo-title">{formatMessage(Messages.splitSales_agentCodeLabel)}</div>
            <div>{splitData.agentCode}</div>
          </div>
          {pageMode === PresentationMode.Loading ? (
            <LoaderComponent />
          ) : pageMode === PresentationMode.View || pageMode === PresentationMode.Error ? (
            <>
              <div>
                <div className="splitSales-primaryInfo-title">{formatMessage(Messages.splitSales_coAgentLabel)}</div>
                <div>{editedSplitData.coAgentCode}</div>
              </div>
              <div>
                <div className="splitSales-primaryInfo-title">
                  {formatMessage(Messages.splitSales_splitPercentageLabel)}
                </div>
                <div>{getLabelForSplitShare(editedSplitData.share)}</div>
              </div>
            </>
          ) : pageMode === PresentationMode.Edit ? (
            <>
              <div>
                <div className="splitSales-primaryInfo-title">{formatMessage(Messages.splitSales_coAgentLabel)}</div>
                <div>{editedSplitData.coAgentCode}</div>
              </div>
              <div>
                <label>{formatMessage(Messages.splitSales_splitPercentageLabel) + '*'}</label>
                <SplitSalesShareSelector editedSplitData={editedSplitData} setEditedSplitData={setEditedSplitData} />
              </div>
            </>
          ) : pageMode === PresentationMode.Add ? (
            <>
              <SplitSalesSearch
                key={searchKey}
                editedSplitData={editedSplitData}
                setEditedSplitData={setEditedSplitData}
              />
              <div>
                <label>{formatMessage(Messages.splitSales_splitPercentageLabel) + '*'}</label>
                <SplitSalesShareSelector editedSplitData={editedSplitData} setEditedSplitData={setEditedSplitData} />
              </div>
            </>
          ) : (
            <></>
          )}

          {pageMode === PresentationMode.Error && <ErrorMessage message={error} />}

          <div className="splitSales-buttonContainer">
            {pageMode === PresentationMode.View ? (
              <>
                {policyState.toLowerCase() === SplitSalesPolicyState.Draft && (
                  <SecondaryButton
                    text={formatMessage(Messages.common_edit)}
                    onClickEvent={() => {
                      setPageMode(PresentationMode.Edit);
                      setEditedSplitData(splitData);
                    }}
                  />
                )}
              </>
            ) : pageMode === PresentationMode.Edit || pageMode === PresentationMode.Add ? (
              <>
                <SecondaryButton
                  text={formatMessage(Messages.splitSales_clearFields)}
                  onClickEvent={() => {
                    setEditedSplitData({
                      policyVersion: editedSplitData.policyVersion,
                      agentCode: editedSplitData.agentCode,
                      coAgentCode: editedSplitData.coAgentCode,
                    });

                    setSearchKey(x => x + 1);
                  }}
                />
                <PrimaryButton
                  text={
                    pageMode === PresentationMode.Edit
                      ? formatMessage(Messages.common_save)
                      : formatMessage(Messages.common_create)
                  }
                  onClickEvent={onSave}
                  disabled={!canSubmit}
                />
              </>
            ) : (
              <PrimaryButton
                text={formatMessage(Messages.common_ok)}
                onClickEvent={() => {
                  const previousState = !splitData.agentCode ? PresentationMode.Edit : PresentationMode.Add;
                  setPageMode(previousState);
                  setError(null);
                }}
              />
            )}
          </div>
        </div>
      </Form>
    </>
  );
};

export default SplitSalesPage;
